import React, { useState } from "react";
import "./MonthlyChart.css";

/**
 * 올해 월별 독서 완료.
 *
 * 선으로 그리면 기록이 없는 달을 0 으로 이어버린다. 9월에 처음 기록한
 * 사람은 1월부터 8월까지 선이 바닥을 기어서, "여덟 달 동안 한 권도 안
 * 읽었다" 로 읽힌다. 사실은 그때 MUROO 를 안 쓰고 있었을 뿐이다.
 * 그래서 첫 기록부터 마지막 기록까지만 그린다. 사이의 0 은 진짜 0 이다.
 *
 * 폭이 화면에 따라 변하므로 선만 SVG 로 두고 점·숫자는 HTML 로 얹는다.
 * viewBox 를 늘려 맞추면 점이 타원으로 찌그러지고 글자도 같이 커졌다
 * 작아진다. 선은 non-scaling-stroke 로 두께를 지킨다.
 */

/** 점이 테두리에 닿지 않도록 그리는 영역을 안쪽으로 들인다 (%) */
const X0 = 3, X1 = 97, Y0 = 14, Y1 = 86;
const TICKS = [1, 4, 7, 10, 12];

const xOf = (i) => X0 + (i * (X1 - X0)) / 11;

/** "2026.09.04" → 9. 형식이 아니거나 다른 해면 null */
function monthOf(display, year) {
  const m = /^(\d{4})\.(\d{2})\./.exec(String(display ?? ""));
  if (!m || Number(m[1]) !== year) return null;
  return Number(m[2]);
}

export default function MonthlyChart({ books, uid, onSeeAll }) {
  /** 눌린 달. 손가락에는 hover 가 없으니 눌러서 본다. */
  const [active, setActive] = useState(null);

  if (!uid) return null;

  const year = new Date().getFullYear();
  const counts = Array(12).fill(0);
  let total = 0;
  for (const b of books || []) {
    const m = monthOf(b.completedBy?.[uid], year);
    if (!m) continue;
    counts[m - 1]++;
    total++;
  }
  if (!total) return null;

  const first = counts.findIndex((c) => c > 0);
  const last = counts.length - 1 - [...counts].reverse().findIndex((c) => c > 0);
  const max = Math.max(...counts);
  const yOf = (v) => Y1 - (v / max) * (Y1 - Y0);

  const pts = [];
  for (let i = first; i <= last; i++) pts.push({ m: i + 1, v: counts[i], x: xOf(i), y: yOf(counts[i]) });

  return (
    <div className="mchart">
      <div className="mchart-head">
        <span>월별 독서 완료</span>
        {/* 그래프 전체를 누르게 하면 달을 짚어 보는 동작과 부딪힌다.
            권수만 누를 수 있게 하고, 눌린다는 것을 화살표로 알린다. */}
        {onSeeAll ? (
          <button className="mchart-sum mchart-sum-go" onClick={onSeeAll}>
            올해 {total}권 <span aria-hidden="true">→</span>
          </button>
        ) : (
          <span className="mchart-sum">올해 {total}권</span>
        )}
      </div>

      <div className="mchart-plot" onMouseLeave={() => setActive(null)}>
        {pts.length > 1 && (
          <svg
            className="mchart-line"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <polyline
              points={pts.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ")}
              fill="none"
              stroke="#c9a84c"
              strokeWidth="1.6"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              opacity=".9"
            />
          </svg>
        )}

        {pts.map(({ m, v, x, y }) =>
          v > 0 ? (
            <button
              key={m}
              type="button"
              className={`mchart-dot${active === m ? " on" : ""}`}
              style={{ left: `${x}%`, top: `${y}%` }}
              onMouseEnter={() => setActive(m)}
              onClick={() => setActive(active === m ? null : m)}
              aria-label={`${m}월 ${v}권`}
            >
              {active === m && (
                <span className={`mchart-tip${y < 34 ? " below" : ""}`}>{m}월 {v}권</span>
              )}
            </button>
          ) : null
        )}
      </div>

      <div className="mchart-months" aria-hidden="true">
        {TICKS.map((m) => (
          <span key={m} style={{ left: `${xOf(m - 1)}%` }}>{m}</span>
        ))}
      </div>
    </div>
  );
}
